import { Product } from "@/types/product";
import { App, Tag, Flex, Input, TableColumnsType, Tooltip, Button, Modal } from "antd";
import React, { FC, useContext, useEffect, useRef, useState } from "react";
import type { TreeDataNode, TreeProps } from 'antd';
import { ProductListParams, priceList, refuse, approve } from "@/apis/product";
import apis from "@/apis/supplier";
import { priceUpdate } from "@/apis/purchase";
import { useTranslation } from "react-i18next";
import { ExclamationCircleOutlined } from '@ant-design/icons';
// import AddOrEdit from "./AddOrEdit";
import useDataList from "@/hooks/useDataList";
import STableLayout, { DEFAULT_PAGE_SIZE, ITableLayout, TableLayoutRef } from "@/styles/STableLayout";
import { ColumnsType } from "antd/lib/table";
import { CurrencySelect } from "@/components/Selector";
import { AnyObject } from "antd/lib/_util/type";
import { CustomTabPaneContext } from "@/components/TabPane";
import { dateFormat, priceFormat } from "@/utils/format";
import PriceForm from "../Supplier/pages/Price/PriceForm";
import SupplierPriceForm from "@/components/SupplierPriceForm";
import useCategories from "../hooks/useCategories";
import ExportExcelButton from "@/components/ExportExcelButton";
import ExportProgressModal from "@/components/ExportProgressModal";

import iconImage from "@/assets/images/fileIcon/image.svg";
import iconFolder from "@/assets/images/fileIcon/folder.svg";

import SText from "@/styles/SText";
import { flushSync } from "react-dom";
import _ from "lodash";
import dayjs from "dayjs";
import { Spin } from "antd/lib";
import ListFilterBar from "@/components/ListFilterBar";
import useCommittedFilters from "@/components/ListFilterBar/hooks/useCommittedFilters";
import WMSAvatar from "@/styles/WMSAvatar";
import { getUserAvatarTip, getUserName } from "@/utils/user";
interface ProductListProps {
  hiddenColumns?: Array<keyof Product>;
  categoryId?: string;
  selectType?: 'multi' | 'select'
}

interface PriceManageFilters {
  keyword: string;
}

const INITIAL_PRICE_MANAGE_FILTERS: PriceManageFilters = {
  keyword: "",
};

const { confirm } = Modal;

const ProductList: FC<ProductListProps> = ({
  hiddenColumns = [],
  categoryId,
  selectType
}) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const { onOpen } = useContext(CustomTabPaneContext);

  const tableRef = useRef<TableLayoutRef | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const {
    draftFilters,
    appliedFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  } = useCommittedFilters<PriceManageFilters>(INITIAL_PRICE_MANAGE_FILTERS);
  const { keyword } = draftFilters;
  const [isOpenAdd, setIsOpenAdd] = useState<boolean>(false);
  const [isOpenEdit, setIsOpenEdit] = useState<boolean>(false);
  const [editRow, setEditRow] = useState<Product | null>(null);
  const [selectProIds, setSelectProIds] = useState<Array<string>>([]);
  const [selectPros, setSelectPros] = useState<Array<Product>>([]);
  const [record, setRecord] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [addLoading, setAddLoading] = useState<boolean>(false);
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [exportProgress, setExportProgress] = useState<number>(0);
  const [list, total, loading, { fetchList, addItem, updateItem, rmItem }] = useDataList<Product, [ProductListParams]>({ fetchApi: priceList });

  // ✅ 组件卸载时取消请求
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    const nextFilters = replaceFilters(INITIAL_PRICE_MANAGE_FILTERS);
    fetchProducts(0, nextFilters);
    tableRef.current?.resetTablePage();
    // 切换分类时重置筛选，不能跟随每次 render 生成的 fetchProducts 重复请求。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, replaceFilters]);

  const columns: TableColumnsType<any> = [
    {
      width: 100,
      title: t("product.code"),
      dataIndex: ["product", "code"],
      hidden: hiddenColumns.includes('code'),
      sorter: (a, b) => {
        const valA = a?.product?.code ?? "";
        const valB = b?.product?.code ?? "";
        return valA.localeCompare(valB);
      },
      render: (_, product) => <SText style={{ width: '100%' }} text={product?.product?.code} />
    },
    {
      width: 80,
      title: t("product.name"),
      dataIndex: "productName",
      hidden: hiddenColumns.includes('name'),
      render: (_, product) => <SText style={{ width: '100%' }} text={product?.product?.name} />
    },
    {
      width: 80,
      title: t("product.brand1"),
      dataIndex: "productModel1",
      hidden: hiddenColumns.includes('model'),
      render: (_, product) => <SText style={{ width: '100%' }} text={product?.product?.model} />
    },
    {
      width: 80,
      title: t("purchase.stPrice"),
      dataIndex: "price",
      hidden: hiddenColumns.includes('model'),
      render: (_, product) => <SText style={{ width: '100%' }} text={product?.price} />
    },
    {
      width: 80,
      title: t("purchase.expiredAt"),
      dataIndex: "expiredAt",
      hidden: hiddenColumns.includes('model'),
      render: (_, product) => <SText style={{ width: '100%' }} text={dateFormat(dayjs(product?.expiredAt))} />
    },
    {
      width: 80,
      title: t("purchase.days"),
      dataIndex: "days",
      hidden: hiddenColumns.includes('model'),
      render: (_, product) => <SText style={{ width: '100%' }} text={product.days} />
    },
    {
      width: 80,
      title: t("purchase.mpq"),
      dataIndex: "mpq",
      hidden: hiddenColumns.includes('model'),
      render: (_, product) => <SText style={{ width: '100%' }} text={product.mpq} />
    },
    {
      title: <SText text={t("purchase.currency")} />,
      dataIndex: "currency",
      width: 80,
      sorter: (a, b) => {
        const valA = a?.currency ?? "";
        const valB = b?.currency ?? "";
        return valA.localeCompare(valB);
      },
      render: (val, item) => (
        <CurrencySelect key={item.id} value={val || ""} readOnly={true} />
      ),
    },
    {
      width: 80,
      title: t("purchase.code"),
      dataIndex: ["supplier", "code"],
      hidden: hiddenColumns.includes('model'),
      sorter: (a, b) => {
        const valA = a?.supplier?.code ?? "";
        const valB = b?.supplier?.code ?? "";
        return valA.localeCompare(valB);
      },
      render: (_, product) => <SText style={{ width: '100%' }} text={product.supplier.code} />
    },
    {
      width: 80,
      title: t("purchase.name"),
      dataIndex: "supplierCode",
      hidden: hiddenColumns.includes('model'),
      render: (_, product) => <SText style={{ width: '100%' }} text={product.supplier.name} />
    },
    {
      title: <SText text={t("common.creator")} />,
      dataIndex: "creator",
      width: 100,
      render: (_, item) => {
        let userData = item.creator;
        return (
          userData && <Flex>
            <div>
              <WMSAvatar creator={userData} uniChar={userData.id}
                avatarId={userData.avatar}
                size={24} alt={getUserAvatarTip(userData)}
              />
            </div>
            <SText text={getUserName(userData)} />
          </Flex>
        )
      },
    },
    {
      width: 80,
      dataIndex: "gallery",
      title: t("purchase.state"),
      fixed: "right",
      render: (_, item) => (
        <Flex align="center" justify="center" style={{ width: "100%" }}>
          {item.approveState === 1 ? (
            <Tag color="green">{t("requirement.passed")}</Tag>
          ) : item.approveState === 0 ? (
            <Tag color="red">{t("requirement.rejected")}</Tag>
          ) : (
            <Tag color="blue">{t("sale.notApproval")}</Tag>
          )}
        </Flex>
      ),
    },
    {
      width: 80,
      dataIndex: "folder",
      title: t("purchase.operate"),
      fixed: "right",
      render: (_, item) => (
        <Flex align="center" justify="center" style={{ width: "100%" }}>
          <a onClick={() => {
            setRecord(item)
          }}>{t("common.detail")}</a>
        </Flex>
      ),
    },
  ];

  const fetchProducts = (offset: number, filters: PriceManageFilters) => {
    // ✅ 取消上一次请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // ✅ 创建新的 AbortController
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;

    fetchList({
      categoryId: '',
      params: {
        body: filters.keyword,
        pagination: { offset, take },
        controller, // ✅ 传递 controller 用于取消请求
      }
    }).catch((err) => {
      // ✅ 忽略取消的请求
      if (err?.name === 'AbortError') {
        return;
      }
    });
  };

  // 导出列配置
  const exportColumns = (t: any) => [
    { key: "product.code", label: t("product.code") },
    { key: "product.name", label: t("product.name") },
    { key: "product.brand1", label: t("product.brand1") },
    { key: "purchase.stPrice", label: t("purchase.stPrice") },
    { key: "purchase.expiredAt", label: t("purchase.expiredAt") },
    { key: "purchase.days", label: t("purchase.days") },
    { key: "purchase.mpq", label: t("purchase.mpq") },
    { key: "purchase.currency", label: t("purchase.currency") },
    { key: "purchase.code", label: t("purchase.code") },
    { key: "purchase.name", label: t("purchase.name") },
    { key: "purchase.state", label: t("purchase.state") },
  ];

  // 默认选中的列
  const defaultSelectedColumns = (t: any) => [
    'product.code',
    'product.name',
    'product.brand1',
    'purchase.stPrice'
  ];

  // 导出数据映射函数
  const exportDataMapper = (item: any, t: any) => {
    const getApproveState = (state: number) => {
      if (state === 1) return t('requirement.passed');
      if (state === 0) return t('requirement.rejected');
      return t('sale.notApproval');
    };

    return {
      [t('product.code')]: item?.product?.code || '',
      [t('product.name')]: item?.product?.name || '',
      [t('product.brand1')]: item?.product?.model || '',
      [t('purchase.stPrice')]: item?.price || '',
      [t('purchase.expiredAt')]: item?.expiredAt ? dateFormat(dayjs(item.expiredAt)) : '',
      [t('purchase.days')]: item?.days || '',
      [t('purchase.mpq')]: item?.mpq || '',
      [t('purchase.currency')]: item?.currency || '',
      [t('purchase.code')]: item?.supplier?.code || '',
      [t('purchase.name')]: item?.supplier?.name || '',
      [t('purchase.state')]: getApproveState(item?.approveState),
    };
  };

  const handleExportAll = async (): Promise<any[]> => {
    setExportLoading(true);
    setExportProgress(0);

    try {
      const res = await priceList({
        categoryId: '', params: {
          body: appliedFilters.keyword,
        }
      });
      const products = res?.body ?? [];
      setExportProgress(100);
      return products;
    } catch (error) {
      notification.error({ placement: 'top', message: t('common.exportErr') });
      throw error;
    } finally {
      setExportLoading(false);
      setExportProgress(0);
    }
  };

  const handleSearch = () => {
    const nextFilters = commitFilters();
    fetchProducts(0, nextFilters);
    tableRef.current?.resetTablePage();
  };

  const handleClearFilters = () => {
    const nextFilters = replaceFilters(INITIAL_PRICE_MANAGE_FILTERS);
    fetchProducts(0, nextFilters);
    tableRef.current?.resetTablePage();
  };

  const headerChildren = (
    <Flex style={{ width: "100%" }} justify="space-between">
      <Flex gap={10}>
        <ExportExcelButton
          list={list}
          columns={exportColumns}
          defaultSelectedColumns={defaultSelectedColumns}
          dataMapper={exportDataMapper}
          fileName="quotation_products.xlsx"
          sheetName={t('purchase.priceManage')}
        />
        <ExportExcelButton
          list={list}
          isAll
          loadData={handleExportAll}
          columns={exportColumns}
          defaultSelectedColumns={defaultSelectedColumns}
          dataMapper={exportDataMapper}
          fileName="quotation_products_all.xlsx"
          sheetName={t('purchase.priceManage')}
        />
      </Flex>

      <div style={{ flex: 1, minWidth: 0 }}>
        <ListFilterBar
          onSearch={handleSearch}
          onReset={handleClearFilters}
          disabled={loading}
        >
          {({ submit }) => (
            <Tooltip>
              <Input
                value={keyword}
                onChange={event =>
                  updateDraftFilters({ keyword: event.target.value.trim() })
                }
                onPressEnter={submit}
                placeholder={t("order.searchPl")}
                style={{ width: 200 }}
              />
            </Tooltip>
          )}
        </ListFilterBar>
      </div>
    </Flex>
  );

  const getTableLayout = () => {
    let props: ITableLayout = {
      loading,
      dataSource: list,
      columns: columns as ColumnsType<AnyObject>,
      headerChildren,
      tableLayoutRef: tableRef,
      total,
      handleChange: (offset: number) =>
        fetchProducts(offset, appliedFilters)
    }

    if (selectType === 'multi') {
      props = {
        ...props,
        rowKey: 'id',
        rowSelection: {
          type: "checkbox",
          selectedRowKeys: selectProIds,
          onChange: (val, n) => {
            flushSync(() => {
              setSelectProIds(() =>
                Array.from(new Set(val as Array<string>))
              );
              setSelectPros(() => _.uniqBy(n as Array<Product>, "id"));
            });
          },
          getCheckboxProps: (record) => ({
            name: record.id,
            // disabled: !!values.find((item) => item?.id === record.id),
          }),
        }
      }
    }

    const TableEle = <STableLayout {...props} />
    return TableEle
  }

  const onHandle = async (type: string) => {
    confirm({
      title: t("common.tip"),
      content: type === 'onApprove' ? t("purchase.onOk") : t("purchase.onReject"),
      icon: <ExclamationCircleOutlined />,
      okText: t("purchase.ok"),
      cancelText: t("purchase.cancel"),
      cancelButtonProps: { style: { color: 'black', height: 24, fontSize: 14 } },
      okButtonProps: { style: { color: 'white', height: 24, background: '#7a294f', fontSize: 14 } },
      onOk() {
        submit(type)
      },
    });

  }

  const submit = async (type: string) => {
    setIsLoading(true)
    let result
    if (type === 'refuce') {
      result = await refuse(record.id)
      notification.success({ placement: 'top', message: t('requirement.rejected') })
    } else {
      result = await approve(record.id)
      notification.success({ placement: 'top', message: t('requirement.oked') })
    }
    if (result) {
      if (type === 'onApprove') {
        apis.supplierPrices(record.supplier.id).then((res) => {
          let params = {
            days: record.days,
            effectiveAt: Date.now(),
            expiredAt: record.expiredAt,
            mpq: record.mpq,
            price: record.price,
            productId: record.productId,
            currency: record.currency,
            remark: record.remard,
            ladders: [{ price: record.price, count: record.mpq, discount: '100', remark: record.remark }]
          }
          let priceId: any = null
          res.body.map(item => {
            if (item.productId === record.productId) {
              priceId = item.priceId
            }
          })
          if (priceId) {
            priceId && priceUpdate(priceId, params)
          } else {
            apis.supplierPriceAdd(record.supplier.id, params)
          }
        })
      }
      setRecord(null)
      fetchProducts(0, appliedFilters);
      tableRef.current?.resetTablePage();
    }
    setIsLoading(false)
  }

  const btnList = () => (
    <div style={{ textAlign: 'right', paddingBottom: 10 }}>
      <Button onClick={() => onHandle('refuce')} style={{ marginRight: 20 }} >{t("purchase.reject")}</Button>
      <Button onClick={() => onHandle('onApprove')} type={'primary'}>{t("common.pass")}</Button>
    </div>
  )


  return (
    <Flex vertical style={{ width: "100%", height: "100%" }}>
      {getTableLayout()}
      {record &&
        <Spin spinning={isLoading}>
          <SupplierPriceForm
            propProductList={[record.product]}
            open={true}
            confirmLoading={isLoading}
            handleCancel={() => setRecord(null)}
            handleOk={() => new Promise((resolve, reject) => { })}
            leftTitle={t("purchase.priceManage")}
            type={'examine'}
            id={record.id || ''}
            btnList={btnList}
          />
        </Spin>}
      <ExportProgressModal visible={exportLoading} progress={exportProgress} />
    </Flex>
  );
};

export default ProductList;
